import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileText,
  Plus,
  Trash2,
  Printer,
  CreditCard,
  Receipt,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { invoiceService } from '@/services/invoiceService';
import { PaymentRecordModal } from './PaymentRecordModal';
import { InvoicePrintModal } from './InvoicePrintModal';
import { ReceiptPrintModal } from './ReceiptPrintModal';
import { formatDateTime } from '@/utils/dateTime';
import { toast } from 'sonner';

export const InvoiceModal = ({
  open,
  onOpenChange,
  consultation,
  patient,
  doctor,
  clinicId,
  hospitalId,
  onInvoiceCreated,
}) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([
    {
      description: 'General OPD Consultation Fee',
      itemType: 'CONSULTATION',
      quantity: 1,
      unitPrice: 500,
      amount: 500,
    },
  ]);
  const [discount, setDiscount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);

  // Submodals
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [printInvoiceOpen, setPrintInvoiceOpen] = useState(false);
  const [receiptPrintOpen, setReceiptPrintOpen] = useState(false);
  const [selectedPaymentForReceipt, setSelectedPaymentForReceipt] = useState(null);

  useEffect(() => {
    if (open && consultation) {
      loadExistingInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, consultation]);

  const loadExistingInvoice = async () => {
    try {
      setLoading(true);
      const existing = await invoiceService.getByConsultationId(consultation.id, clinicId, hospitalId);
      if (existing) {
        setInvoice(existing);
        setItems(existing.items || []);
        setDiscount(existing.discount || 0);
        setTaxAmount(existing.taxAmount || 0);
      } else {
        setInvoice(null);
        setItems([
          {
            description: 'General OPD Consultation Fee',
            itemType: 'CONSULTATION',
            quantity: 1,
            unitPrice: 500,
            amount: 500,
          },
        ]);
        setDiscount(0);
        setTaxAmount(0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        description: '',
        itemType: 'PROCEDURE',
        quantity: 1,
        unitPrice: 200,
        amount: 200,
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const copy = [...prev];
      const it = { ...copy[index], [field]: value };
      const q = field === 'quantity' ? Number(value) || 0 : Number(it.quantity) || 0;
      const p = field === 'unitPrice' ? Number(value) || 0 : Number(it.unitPrice) || 0;
      it.amount = q * p;
      copy[index] = it;
      return copy;
    });
  };

  const subtotal = items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
  const totalAmount = Math.max(0, subtotal - (Number(discount) || 0) + (Number(taxAmount) || 0));

  const handleCreateInvoice = async () => {
    const validItems = items.filter((it) => it.description.trim().length > 0 && it.unitPrice >= 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one valid billing item');
      return;
    }

    try {
      setLoading(true);
      const created = await invoiceService.createForConsultation(
        consultation.id,
        {
          clinicId,
          patientId: patient?.id || consultation.patientId,
          patientName: patient ? `${patient.firstName} ${patient.lastName}` : consultation.patientName,
          patientCode: patient?.patientCode || consultation.patientCode,
          doctorId: doctor?.id || consultation.doctorId,
          doctorName: doctor?.name || consultation.doctorName,
          items: validItems,
          discount: Number(discount) || 0,
          taxAmount: Number(taxAmount) || 0,
        },
        hospitalId
      );

      setInvoice(created);
      toast.success(`Invoice ${created.invoiceNumber} generated!`);
      if (onInvoiceCreated) onInvoiceCreated(created);
    } catch (e) {
      toast.error('Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (updatedInvoice, newPayment) => {
    setInvoice(updatedInvoice);
    setSelectedPaymentForReceipt(newPayment);
    if (onInvoiceCreated) onInvoiceCreated(updatedInvoice);
  };

  const handleOpenReceipt = (payment) => {
    setSelectedPaymentForReceipt(payment);
    setReceiptPrintOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PAID':
        return <Badge className="bg-emerald-600 text-white font-bold">PAID</Badge>;
      case 'PARTIALLY_PAID':
        return <Badge className="bg-amber-500 text-white font-bold">PARTIALLY PAID</Badge>;
      default:
        return <Badge variant="outline" className="border-red-400 text-red-600 font-bold bg-red-50">PENDING</Badge>;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[88vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-5 bg-slate-900 text-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-base font-bold text-white flex items-center gap-2">
                  <FileText className="h-5 w-5 text-teal-400" />
                  {invoice ? `Invoice ${invoice.invoiceNumber}` : 'Generate Clinical Invoice'}
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-300">
                  Patient:{' '}
                  <strong className="text-white">
                    {patient ? `${patient.firstName} ${patient.lastName}` : consultation?.patientName || 'Patient'}
                  </strong>{' '}
                  ({patient?.patientCode || consultation?.patientCode || 'N/A'})
                </DialogDescription>
              </div>
              {invoice && <div>{getStatusBadge(invoice.status)}</div>}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Items Table */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Billable Line Items ({items.length})
                </Label>
                {!invoice && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddItem}
                    className="text-xs border-teal-300 text-teal-700 h-7"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Item
                  </Button>
                )}
              </div>

              <div className="space-y-2 border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                {items.map((it, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center bg-white p-2.5 rounded-lg border border-slate-200"
                  >
                    <div className="sm:col-span-5 space-y-1">
                      <Input
                        disabled={!!invoice}
                        placeholder="Description (e.g. Consultation Fee)"
                        value={it.description}
                        onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                        className="text-xs"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Select
                        disabled={!!invoice}
                        value={it.itemType}
                        onValueChange={(val) => handleItemChange(idx, 'itemType', val)}
                      >
                        <SelectTrigger className="text-xs h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CONSULTATION">Consultation</SelectItem>
                          <SelectItem value="PROCEDURE">Procedure</SelectItem>
                          <SelectItem value="LAB">Lab Test</SelectItem>
                          <SelectItem value="MEDICINE">Medicine</SelectItem>
                          <SelectItem value="SERVICE">Service</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-1">
                      <Input
                        disabled={!!invoice}
                        type="number"
                        min="1"
                        placeholder="Qty"
                        value={it.quantity}
                        onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                        className="text-xs text-center"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                          ₹
                        </span>
                        <Input
                          disabled={!!invoice}
                          type="number"
                          placeholder="Price"
                          value={it.unitPrice}
                          onChange={(e) => handleItemChange(idx, 'unitPrice', e.target.value)}
                          className="text-xs pl-6"
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2 flex items-center justify-between">
                      <span className="text-xs font-mono font-bold text-slate-900">
                        ₹{(Number(it.amount) || 0).toFixed(2)}
                      </span>
                      {!invoice && items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveItem(idx)}
                          className="h-7 w-7 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Financial Summary Card */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row justify-between gap-4">
              <div className="space-y-2 flex-1 text-xs">
                <div className="grid grid-cols-2 gap-2 max-w-xs">
                  <div>
                    <Label className="text-[11px] text-slate-600">Discount (₹)</Label>
                    <Input
                      disabled={!!invoice}
                      type="number"
                      min="0"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      className="text-xs h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-600">Tax / GST (₹)</Label>
                    <Input
                      disabled={!!invoice}
                      type="number"
                      min="0"
                      value={taxAmount}
                      onChange={(e) => setTaxAmount(e.target.value)}
                      className="text-xs h-8"
                    />
                  </div>
                </div>
              </div>

              <div className="w-64 space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal:</span>
                  <span className="font-mono">₹{subtotal.toFixed(2)}</span>
                </div>
                {Number(discount) > 0 && (
                  <div className="flex justify-between text-emerald-600 font-medium">
                    <span>Discount:</span>
                    <span className="font-mono">-₹{Number(discount).toFixed(2)}</span>
                  </div>
                )}
                {Number(taxAmount) > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Tax:</span>
                    <span className="font-mono">₹{Number(taxAmount).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold text-slate-950 border-t border-slate-200 pt-1.5">
                  <span>Total Amount:</span>
                  <span className="font-mono text-teal-800">
                    ₹{invoice ? Number(invoice.totalAmount).toFixed(2) : totalAmount.toFixed(2)}
                  </span>
                </div>
                {invoice && (
                  <>
                    <div className="flex justify-between text-emerald-700 font-semibold pt-1 border-t border-dashed border-slate-200">
                      <span>Paid to Date:</span>
                      <span className="font-mono">₹{Number(invoice.paidAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-bold text-red-600">
                      <span>Remaining Balance:</span>
                      <span className="font-mono">₹{Number(invoice.balanceAmount).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Payment Transactions Section (if invoice exists) */}
            {invoice && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Receipt className="h-4 w-4 text-emerald-600" />
                    Payment History ({invoice.payments?.length || 0})
                  </Label>
                  {Number(invoice.balanceAmount) > 0 && (
                    <Button
                      size="sm"
                      onClick={() => setPaymentModalOpen(true)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-3 font-semibold shadow-sm"
                    >
                      <CreditCard className="h-3.5 w-3.5 mr-1" /> Record Payment
                    </Button>
                  )}
                </div>

                {(!invoice.payments || invoice.payments.length === 0) ? (
                  <div className="p-4 text-center bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    No payments recorded yet. Remaining balance is <strong>₹{Number(invoice.balanceAmount).toFixed(2)}</strong>.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {invoice.payments.map((pay, pIdx) => (
                      <div
                        key={pay.id || pIdx}
                        className="p-3 bg-white border border-slate-200 rounded-lg flex items-center justify-between text-xs shadow-sm"
                      >
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900">{pay.paymentMode}</span>
                            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                              {pay.receiptNumber}
                            </Badge>
                          </div>
                          <p className="text-[11px] text-slate-500">
                            {formatDateTime(pay.paymentDate)}
                            {pay.referenceNumber && <span className="ml-2 font-mono">Ref: {pay.referenceNumber}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-bold text-sm text-emerald-700">
                            ₹{Number(pay.amount).toFixed(2)}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenReceipt(pay)}
                            className="h-7 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                          >
                            <Receipt className="h-3 w-3 mr-1" /> View Receipt
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="text-xs">
              Close
            </Button>
            <div className="flex items-center gap-2">
              {invoice ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPrintInvoiceOpen(true)}
                    className="text-xs border-slate-300"
                  >
                    <Printer className="h-3.5 w-3.5 mr-1.5" /> Print Invoice
                  </Button>
                  {Number(invoice.balanceAmount) > 0 && (
                    <Button
                      size="sm"
                      onClick={() => setPaymentModalOpen(true)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold"
                    >
                      <CreditCard className="h-3.5 w-3.5 mr-1.5" /> Collect Payment
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  size="sm"
                  disabled={loading}
                  onClick={handleCreateInvoice}
                  className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold shadow-sm"
                >
                  <FileText className="h-3.5 w-3.5 mr-1.5" />
                  {loading ? 'Generating...' : 'Generate Invoice'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Recording Submodal */}
      {invoice && (
        <PaymentRecordModal
          open={paymentModalOpen}
          onOpenChange={setPaymentModalOpen}
          invoice={invoice}
          onPaymentSuccess={handlePaymentSuccess}
          onViewReceipt={handleOpenReceipt}
        />
      )}

      {/* Invoice Print / PDF Submodal */}
      {invoice && (
        <InvoicePrintModal
          open={printInvoiceOpen}
          onOpenChange={setPrintInvoiceOpen}
          invoice={invoice}
          patient={patient}
          doctor={doctor}
        />
      )}

      {/* Payment Receipt Submodal */}
      {selectedPaymentForReceipt && (
        <ReceiptPrintModal
          open={receiptPrintOpen}
          onOpenChange={setReceiptPrintOpen}
          payment={selectedPaymentForReceipt}
          invoice={invoice}
          patient={patient}
        />
      )}
    </>
  );
};

export default InvoiceModal;

